import { useState, useCallback, useEffect } from 'react';
import useAppContext from '../../../hooks/useAppContext';
import useAnalytics from '../../../hooks/useAnalytics';
import useClockDataListener from './useClockDataListener';
import useAddClockToUserMutation from './useAddClockToUserMutation';
import usePlayKlydoOnClockMutation from './usePlayKlydoOnClockMutation';
import useUpdateClockApplicationMutation from './useUpdateClockApplicationMutation';
import { AlertType } from '../../../design-system/DSAlert';
import { PreviewUnavailableReason } from '../components/PreviewUnavailableModal';
import firebaseService from '../../../firebase/firebaseService';

type UsePreviewOnClockProps = {
  klydoId: string | undefined;
};

const MINIMUM_CLOCK_VERSION = 604.5;

const usePreviewOnClock = ({ klydoId }: UsePreviewOnClockProps) => {
  const { user, isAdmin, isViewer, clock, refreshToken, showAlert } =
    useAppContext();
  const { trackEvent } = useAnalytics();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewUnavailableReason, setPreviewUnavailableReason] =
    useState<PreviewUnavailableReason | null>(null);

  const canPreview = isAdmin || isViewer || user?.type === 'Artist';

  const { data: clockData, isLoading: isLoadingClockData } =
    useClockDataListener(canPreview ? clock : null);

  const [pendingPlayCommandId, setPendingPlayCommandId] = useState<
    string | null
  >(null);
  const [pendingUpdateCommandId, setPendingUpdateCommandId] = useState<
    string | null
  >(null);

  const clockIdf = clockData?.idf;

  useEffect(() => {
    if (!pendingPlayCommandId || !clock) {
      return;
    }

    const unsubscribe = firebaseService.listenToCommandStatus(
      clock,
      pendingPlayCommandId,
      (isCompleted) => {
        if (isCompleted) {
          setPendingPlayCommandId(null);
        }
      },
    );

    return () => unsubscribe();
  }, [pendingPlayCommandId, clock]);

  useEffect(() => {
    if (!pendingUpdateCommandId || !clock) {
      return;
    }

    const unsubscribe = firebaseService.listenToCommandStatus(
      clock,
      pendingUpdateCommandId,
      (isCompleted) => {
        if (isCompleted) {
          setPendingUpdateCommandId(null);
        }
      },
    );

    return () => unsubscribe();
  }, [pendingUpdateCommandId, clock]);

  const buttonText =
    clock && clockIdf ? `Preview On ${clockIdf}` : 'Preview On Klydoclock';

  const { mutate: playKlydo, isPending: isPlayKlydoPending } =
    usePlayKlydoOnClockMutation({
      onSuccess: (commandId) => {
        if (commandId) {
          setPendingPlayCommandId(commandId);
          showAlert({
            message: 'Your klydo is now playing on your clock!',
            alertType: AlertType.SUCCESS,
          });
        } else {
          showAlert({
            message: 'Your klydo will appear on your clock in a few seconds!',
            alertType: AlertType.SUCCESS,
          });
        }
      },
    });

  const { mutate: updateClock, isPending: isUpdating } =
    useUpdateClockApplicationMutation({
      onSuccess: (commandId) => {
        setPendingUpdateCommandId(commandId);
        setPreviewUnavailableReason(null);
        showAlert({
          message: 'Clock update has been initiated',
          alertType: AlertType.SUCCESS,
        });
      },
    });

  const { mutate: addClockMutation, isPending: isAddClockPending } =
    useAddClockToUserMutation({
      onSuccess: async () => {
        await refreshToken();

        setIsModalOpen(false);

        showAlert({
          message: 'Your device has been connected',
          alertType: AlertType.SUCCESS,
        });

        if (klydoId && clock) {
          playKlydo({
            clockId: clock,
            klydoId,
            clockVersion: clockData?.version,
          });
        }
      },
    });

  const handlePreviewClick = useCallback(() => {
    if (!klydoId) return;

    trackEvent({
      type: 'klydo_previewed_on_clock',
      context: `klydoId:${klydoId}`,
    });

    if (!clock) {
      setIsModalOpen(true);
      return;
    }

    if (clockData && clockData.connected !== true) {
      setPreviewUnavailableReason(PreviewUnavailableReason.NOT_CONNECTED);
      return;
    }

    if (
      clockData &&
      clockData.version &&
      clockData.version < MINIMUM_CLOCK_VERSION
    ) {
      setPreviewUnavailableReason(PreviewUnavailableReason.NEEDS_UPDATE);
      return;
    }

    playKlydo({ clockId: clock, klydoId, clockVersion: clockData?.version });
  }, [clock, klydoId, clockData, playKlydo, trackEvent]);

  const handleConnectClock = useCallback(
    async (serialNumber: string) => {
      if (!klydoId) return;

      addClockMutation({
        clockId: serialNumber,
      });
    },
    [klydoId, addClockMutation],
  );

  const handleUpdateClock = useCallback(() => {
    if (!clock) return;
    updateClock({ clockId: clock });
  }, [clock, updateClock]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleClosePreviewUnavailable = useCallback(() => {
    setPreviewUnavailableReason(null);
  }, []);

  const isCommandPending = !!pendingPlayCommandId || !!pendingUpdateCommandId;
  const isDisabled =
    isCommandPending || isPlayKlydoPending || isLoadingClockData;

  const tooltipText = isCommandPending
    ? pendingUpdateCommandId
      ? 'Clock is updating, please wait...'
      : 'Previous play command is still processing...'
    : undefined;

  return {
    canPreview,
    buttonText,
    isModalOpen,
    isConnecting: isAddClockPending,
    isLoading: isPlayKlydoPending || isLoadingClockData,
    isDisabled,
    tooltipText,
    handlePreviewClick,
    handleConnectClock,
    handleCloseModal,
    previewUnavailableReason,
    handleUpdateClock,
    handleClosePreviewUnavailable,
    isUpdating,
  };
};

export default usePreviewOnClock;
